import React from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { TextFieldProps } from '@mui/material';

interface IsoDateFieldProps {
  label?: string;
  /** ISO 'YYYY-MM-DD' value ('' when unset). */
  value: string;
  /** Called with the ISO 'YYYY-MM-DD' value, or '' when cleared. */
  onChange: (iso: string) => void;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: TextFieldProps['size'];
  sx?: TextFieldProps['sx'];
  inputProps?: TextFieldProps['inputProps'];
}

/**
 * IsoDateField — shared date input that stores an ISO 'YYYY-MM-DD' string.
 *
 * Uses the MUI X masked field instead of a native <input type="date">: the
 * native control lets focus land on the year segment, where typed digits
 * accumulate into a 6-digit year (e.g. 'mm/dd/202026'). The masked field
 * auto-advances month → day → year and clamps the year to 4 digits, so
 * typing a date like 10222026 always yields 10/22/2026.
 */
export default function IsoDateField({
  label,
  value,
  onChange,
  disabled,
  required,
  fullWidth,
  size = 'small',
  sx,
  inputProps,
}: IsoDateFieldProps) {
  const dayjsValue: Dayjs | null = value ? dayjs(value, 'YYYY-MM-DD') : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={dayjsValue && dayjsValue.isValid() ? dayjsValue : null}
        onChange={(date) => onChange(date && dayjs.isDayjs(date) && date.isValid() ? date.format('YYYY-MM-DD') : '')}
        disabled={disabled}
        format="MM/DD/YYYY"
        slotProps={{
          textField: {
            size,
            fullWidth,
            required,
            sx,
            inputProps,
          },
        }}
      />
    </LocalizationProvider>
  );
}
